import { FC } from "react"
import { Box, Typography } from "@mui/material"
import { BackspaceOutlined } from "@mui/icons-material"
import { StaffSchedule } from "../interfaces"
import { normalizeHex, readableTextColor } from "../model/shiftTypeColor"
import { Brush } from "../hooks"

interface BrushPaletteProps {
  legend: Map<number, StaffSchedule.ShiftType>
  countsByShiftType: Map<number, number>
  brush: Brush
  canWrite: boolean
  onToggleShift: (shiftTypeId: number) => void
  onToggleEraser: () => void
}

const itemSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  px: 1,
  py: 0.5,
  border: "2px solid",
  borderRadius: 1,
  userSelect: "none",
} as const

/**
 * Легенда-палитра: клик делает тип смены активной кистью, повторный клик снимает выбор
 * (toggle живёт в useBrush). Счётчики — по видимому периоду.
 * Без права на запись — режим только чтения: обработчики не навешиваются, активного
 * состояния нет, ластик не показывается.
 */
export const BrushPalette: FC<BrushPaletteProps> = ({
  legend,
  countsByShiftType,
  brush,
  canWrite,
  onToggleShift,
  onToggleEraser,
}) => (
  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
    {[...legend.values()].map((t) => {
      const active =
        canWrite && brush?.kind === "shift" && brush.shiftTypeId === t.id
      return (
        <Box
          key={t.id}
          data-testid={`brush-${t.id}`}
          role={canWrite ? "button" : undefined}
          aria-pressed={canWrite ? active : undefined}
          onClick={canWrite ? () => onToggleShift(t.id) : undefined}
          sx={{
            ...itemSx,
            borderColor: active ? "primary.main" : "divider",
            cursor: canWrite ? "pointer" : "default",
          }}
        >
          <Box
            style={{
              backgroundColor: normalizeHex(t.color),
              color: readableTextColor(t.color),
            }}
            sx={{
              width: 18,
              height: 18,
              borderRadius: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
            }}
          >
            {t.short_name}
          </Box>
          <Typography variant="body2">
            {t.emoji} {t.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {countsByShiftType.get(t.id) ?? 0}
          </Typography>
        </Box>
      )
    })}
    {canWrite && (
      <Box
        data-testid="brush-eraser"
        role="button"
        aria-pressed={brush?.kind === "eraser"}
        onClick={onToggleEraser}
        sx={{
          ...itemSx,
          borderColor: brush?.kind === "eraser" ? "primary.main" : "divider",
          cursor: "pointer",
        }}
      >
        <BackspaceOutlined fontSize="small" />
        <Typography variant="body2">Ластик</Typography>
      </Box>
    )}
  </Box>
)
